import { useState } from 'react'

import { Button, Modal, Text } from '@hhgtech/hhg-components/mantine'
import { AspectRatio, Badge, Box, BoxProps, rem } from '@mantine/core'
import { useIntl } from 'react-intl'

import Cart from 'components/atoms/icons/Cart'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { MANTINE_MODAL_TARGET } from 'interfaces/constants'
import { Product } from 'interfaces/types/Ecommerce'
import { currencyFormat } from 'utils/ecommerce'

import useStyles from './index.styles'
import { ProductCardBuyNowModal } from '../productCardBuyNowModal'

type Props = {
  product: Product
  layout?: 'vertical' | 'horizon'
  onAddToCart: (amount: number, sku: number | string) => void
  onBuyNow: (amount: number, sku: number | string, slug: string) => void
  cartBtnColor?: string
} & BoxProps

const ProductCard = ({
  product,
  onAddToCart,
  onBuyNow,
  styles,
  layout = 'vertical',
  className,
  classNames,
  cartBtnColor,
  ...boxProps
}: Props) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const [loading, setLoading] = useState(false)
  const [buyLoading, setBuyLoading] = useState(false)
  const { name, price, isInStock, thumbnail, sku, slug } = product
  const { classes, cx } = useStyles(
    {
      layout,
      hasPromotion: Boolean(price.discountPercent),
    },
    {
      name: 'ProductCard',
      styles,
      classNames,
    },
  )

  const [openModal, setOpenModal] = useState(false)
  const url = `/shop/san-pham/${slug}/`
  const discountBadge = () =>
    price.discountPercent ? (
      <Badge
        className={classes.discountTag}
        size="md"
        color="red"
        variant="filled"
        sx={{
          paddingLeft: '5px',
          paddingRight: '5px',
          height: '22px',
        }}
      >
        -
        {Math.round(price.discountPercent) === 0
          ? Math.ceil(price.discountPercent)
          : Math.round(price.discountPercent)}
        %
      </Badge>
    ) : null

  return (
    <>
      <Box
        className={cx(classes.root, className)}
        data-layout={layout}
        {...boxProps}
      >
        <Box
          component="a"
          href={url}
          target="_blank"
          className={classes.info}
          data-event-category="Ecommerce"
          data-event-action="Open Product Page"
          data-event-label={`https://${domainLocales[locale]}${url}`}
        >
          <Box className={classes.thumbnail}>
            <AspectRatio
              ratio={206 / 164}
              sx={{
                '>img': {
                  objectFit: 'contain',
                },
              }}
            >
              <img
                loading="lazy"
                src={thumbnail}
                alt={name}
                onError={(e) =>
                  (e.currentTarget.src = '/images/default-image.jpg')
                }
              />
            </AspectRatio>
            {layout === 'vertical' && discountBadge()}
          </Box>
          <Box className={classes.mainInfo}>
            {layout === 'horizon' && discountBadge()}
            <Text size="p4" className={classes.name}>
              {name}
            </Text>
            <Box className={classes.prices}>
              <Text size="s4" className={classes.mainPrice}>
                {currencyFormat(price.final, locale, 'VND')}
              </Text>
              {price.regular && price.regular !== price.final ? (
                <Text size="s4" className={classes.originPrice}>
                  {currencyFormat(1000000, locale, 'VND')}
                </Text>
              ) : null}
            </Box>
          </Box>
        </Box>
        <Box className={classes.actions}>
          {!product.isAffiliateProduct && (
            <Button
              variant="light"
              color={cartBtnColor}
              className={classes.cartBtn}
              loading={loading}
              leftIcon={<Cart size={18} color="currentColor" />}
              disabled={!isInStock}
              data-event-category="Ecommerce"
              data-event-action="Add To Cart Click"
              data-event-label={`https://${domainLocales[locale]}${url}`}
              onClick={async (e) => {
                e.preventDefault()
                setLoading(true)
                await onAddToCart?.(1, sku)
                setLoading(false)
              }}
            />
          )}
          <Button
            className={cx(
              product.isAffiliateProduct ? classes.affBuyBtn : classes.buyBtn,
            )}
            disabled={!isInStock}
            loading={buyLoading}
            data-event-category="Ecommerce"
            data-event-action="Buy Now Click"
            data-event-label={`https://${domainLocales[locale]}${url}`}
            onClick={async (e) => {
              e.preventDefault()
              if (product.isAffiliateProduct) {
                ;(product.affiliates || []).length > 0
                  ? setOpenModal(true)
                  : window.open(url)
              } else {
                setBuyLoading(true)
                await onBuyNow?.(1, sku, slug)
                setBuyLoading(false)
              }
            }}
          >
            {isInStock
              ? f({
                  id: 'productInfo.buyNow',
                  defaultMessage: 'Mua ngay',
                })
              : f({
                  id: 'tag.outOfStock',
                  defaultMessage: 'Hết hàng',
                })}
          </Button>
        </Box>
      </Box>
      <Modal
        target={MANTINE_MODAL_TARGET}
        opened={Boolean(openModal)}
        onClose={() => setOpenModal(false)}
        size={rem(572)}
      >
        <ProductCardBuyNowModal
          product={product}
          onClose={() => setOpenModal(false)}
        />
      </Modal>
    </>
  )
}

export { ProductCard }
